import type { ComponentType } from 'react';
import { Box, Typography, CircularProgress } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import {
  ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip as RTooltip, ResponsiveContainer,
} from 'recharts';
import { trackingService, type TrackingStatsRow } from '../services/tracking.service';
import { activityReportWindow, activityReportFilter, fillHourlyGaps } from './activity-report';

// recharts 2.x ships `Bar`/`Line` as class components whose types don't satisfy the JSX
// element signature under @types/react 18.3.x — the same aliasing DashboardPage uses.
type AnyProps = Record<string, unknown>;
const RBar = Bar as unknown as ComponentType<AnyProps>;
const RLine = Line as unknown as ComponentType<AnyProps>;

const VISITS = '#9aa3ad';
const CLICKS = '#2a78d6';
const CONVERSIONS = '#d03b3b';
const REVENUE = '#0ca30c';
const COST = '#d03b3b';
const PROFIT = '#1c1e21';

const hourLabel = (hour: string) => String(hour).slice(11, 13);
const money = (n: number) => `$${Number(n || 0).toLocaleString('en', { maximumFractionDigits: 0 })}`;

// `date_hour`, not `hour`: `hour` buckets by hour OF THE DAY and would fold both days onto
// 24 points. Reads the tracking report, which already owns cost attribution and bots.
export default function ActivityHourlyGraph({ kind, entityId, open }: {
  kind: string;
  entityId: string;
  open: boolean;
}) {
  const { data, isLoading } = useQuery({
    queryKey: ['activity-hourly', kind, entityId],
    queryFn: () => trackingService.getStats({
      ...activityReportWindow(),
      ...activityReportFilter(kind, entityId),
      group_by: 'date_hour',
      sort_by: 'name',
      sort_order: 'asc',
      limit: 200,
    }),
    enabled: open && !!kind && !!entityId,
    staleTime: 60_000,
  });

  if (isLoading) {
    return (
      <Box sx={{ height: 150, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress size={18} />
      </Box>
    );
  }
  const rows = data?.rows ?? [];
  if (!rows.length) return null;

  // Clicks are a subset of visits, so stacking them raw would double-count: the blue segment
  // IS the clicks and the bar's height is still the visits.
  const series = fillHourlyGaps(rows).map((r) => {
    const row = r as Partial<TrackingStatsRow> & { group_key: unknown };
    return {
      hour: String(row.group_key),
      clicks: Number(row.clicks) || 0,
      visitsOnly: Math.max(0, (Number(row.visits) || 0) - (Number(row.clicks) || 0)),
      conversions: Number(row.conversions) || 0,
      revenue: Number(row.revenue) || 0,
      cost: Number(row.cost) || 0,
      profit: Number(row.profit) || 0,
    };
  });

  // Without cost-sync a flat zero line reads as "free traffic" rather than "not measured".
  const hasMoney = series.some((s) => s.revenue !== 0 || s.cost !== 0);

  return (
    <Box sx={{ width: '100%' }}>
      {/* Outside the sized box: inside it ResponsiveContainer takes the same 100% and overflows. */}
      <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 0.25 }}>
        Last 48 hours — bar height is visits, blue is the clicks within them
      </Typography>
      <Box sx={{ height: 140, width: '100%' }}>
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={series} margin={{ top: 4, right: 4, bottom: 4, left: -18 }}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} />
            <XAxis dataKey="hour" tickFormatter={hourLabel} interval={3} tick={{ fontSize: 10 }} />
            <YAxis yAxisId="left" tick={{ fontSize: 10 }} width={44} />
            {/* Headroom: recharts ends the domain at the data max, pinning a one-conversion hour to the top. */}
            <YAxis
              yAxisId="right" orientation="right" tick={{ fontSize: 10 }} width={34}
              allowDecimals={false} domain={[0, (max: number) => Math.max(4, Math.ceil(max * 1.5))]}
            />
            <RTooltip
              labelFormatter={(hour: string) => String(hour).slice(0, 16)}
              contentStyle={{ fontSize: 11 }}
              formatter={(value: number, name: string) => [
                value, name === 'visitsOnly' ? 'Visits (no click)' : name,
              ]}
            />
            <RBar yAxisId="left" dataKey="clicks" stackId="traffic" fill={CLICKS} name="Clicks" />
            <RBar yAxisId="left" dataKey="visitsOnly" stackId="traffic" fill={VISITS} name="Visits" />
            <RLine
              yAxisId="right" type="monotone" dataKey="conversions" name="Conv."
              stroke={CONVERSIONS} strokeWidth={1.5} dot={false}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </Box>

      {hasMoney && (
        <>
          <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.75, mb: 0.25 }}>
            Revenue and cost, with profit — cost only covers flows whose source reports spend
          </Typography>
          <Box sx={{ height: 120, width: '100%' }}>
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={series} margin={{ top: 4, right: 4, bottom: 4, left: -6 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="hour" tickFormatter={hourLabel} interval={3} tick={{ fontSize: 10 }} />
                <YAxis tick={{ fontSize: 10 }} width={52} tickFormatter={money} />
                <RTooltip
                  labelFormatter={(hour: string) => String(hour).slice(0, 16)}
                  contentStyle={{ fontSize: 11 }}
                  formatter={(value: number, name: string) => [money(value), name]}
                />
                <RBar dataKey="revenue" fill={REVENUE} name="Revenue" />
                <RBar dataKey="cost" fill={COST} name="Cost" />
                {/* A line, not a bar: profit goes negative and a bar crossing the axis reads badly. */}
                <RLine
                  type="monotone" dataKey="profit" name="Profit"
                  stroke={PROFIT} strokeWidth={1.5} dot={false}
                />
              </ComposedChart>
            </ResponsiveContainer>
          </Box>
        </>
      )}
    </Box>
  );
}
